"use client";

import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {NextStep, NextStepProvider} from "nextstepjs";
import {Permissions, RoleData} from "@/enums/user";
import {useOrg} from "@/hooks/useOrg";
import {usePermissions} from "@/hooks/usePermissions";
import {canAccessNavItem} from "@/utils/authz/navAccess";
import {CREATE_OR_UPDATE_LEVELS} from "@/utils/authz/routeGates";
import {useDashboardTourCallbacks} from "./DashboardTour";
import {buildDashboardTourSteps, type TourStepVisibility} from "./dashboardTourSteps";
import DashboardTourCard from "./DashboardTourCard";
import TourOnScreenReporter from "@/components/common/tour/TourOnScreenReporter";

export default function DashboardTourProvider({
   children,
   showRecommendedActions = true,
}: Readonly<{children: React.ReactNode; showRecommendedActions?: boolean}>) {
   const {t} = useTranslation("stats");
   const {onComplete, onSkip} = useDashboardTourCallbacks();
   const {productAccess} = useOrg();
   const {canAccess, canAccessAny} = usePermissions();
   const visibility: TourStepVisibility = useMemo(() => {
      const showInbound = canAccess(RoleData.Inbound, Permissions.Read);
      const showOutreach = canAccess(RoleData.Outbound, Permissions.Read);
      const showContacts = canAccess(RoleData.Contacts, Permissions.Read);
      const showCampaigns = canAccess(RoleData.Campaigns, Permissions.Read);

      const showGeneralSection =
         canAccessNavItem(
            {dataType: RoleData.Assistants, levels: CREATE_OR_UPDATE_LEVELS},
            canAccessAny
         ) ||
         canAccessNavItem(
            {
               dataType: RoleData.Knowledge,
               additionalDataTypes: [RoleData.Agent, RoleData.Settings],
               levels: CREATE_OR_UPDATE_LEVELS,
            },
            canAccessAny
         ) ||
         canAccess(RoleData.Integrations, Permissions.Read) ||
         canAccess(RoleData.Workflows, Permissions.Read);

      return {
         showInbound,
         showOutreach,
         showContacts,
         showCampaigns,
         showGailGpt: productAccess.userCanUseGailGpt,
         showGeneralSection,
         showRecommendedActions,
      };
   }, [canAccess, canAccessAny, productAccess.userCanUseGailGpt, showRecommendedActions]);

   const steps = useMemo(() => buildDashboardTourSteps(t, visibility), [t, visibility]);

   return (
      <NextStepProvider>
         <NextStep steps={steps} onComplete={onComplete} onSkip={onSkip} cardComponent={DashboardTourCard}>
            <TourOnScreenReporter />
            {children}
         </NextStep>
      </NextStepProvider>
   );
}
